import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Table,
  Input,
  Row,
  FormGroup,
  Label,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';
import { toast } from 'react-toastify';
import CustomModal from '../../constants/CustomModal';

const LeaveRequestList = () => {
  const { user } = useSelector((state) => state.authState);
  const navigate = useNavigate();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [leaveList, setLeaveList] = useState([]);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
    status: 'pending',
  });

  const [remarkModal, setRemarkModal] = useState({
    show: false,
    id: 0,
    status: '',
    remark: '',
    error: '',
  });

  const isAdminUser =
    String(user?.user_profile || '').toLowerCase() === 'admin' ||
    String(user?.user_profile || '').toLowerCase() === 'superadmin' ||
    isAdmin ||
    isSuperAdmin;

  const onHandleAdd = () => {
    navigate('/save-leave-request');
  };

  const getStatusBadge = (status) => {
    const st = String(status || '').toLowerCase();

    if (st === 'approved') return 'badge bg-success';
    if (st === 'rejected') return 'badge bg-danger';

    return 'badge bg-warning text-dark';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    return new Date(dateValue).toLocaleDateString();
  };

  const GetLeaveList = useCallback(async () => {
    try {
      let res;

      if (isAdminUser) {
        const vendorId = selectionData.vendor_id || '';
        const branchId = selectionData.branch_id || '';
        const status = selectionData.status || 'pending';

        res = await apiService.get(
          `api/AttendanceMaster/all-leaves?vendorId=${vendorId}&branchId=${branchId}&status=${status}`,
        );
      } else {
        res = await apiService.get('api/AttendanceMaster/my-leaves');
      }

      if (res?.status === true) {
        setLeaveList(res.data || []);
      } else {
        toast.error(res?.message || 'Failed');
        setLeaveList([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
      setLeaveList([]);
    }
  }, [
    isAdminUser,
    selectionData.vendor_id,
    selectionData.branch_id,
    selectionData.status,
  ]);

  useEffect(() => {
    if (isAdminUser) {
      const canLoad = conditionalDataLoad({
        hasMultiBranch,
        hasSingleBranch,
        isSuperAdmin,
        isAdmin,
        vendorId: selectionData.vendor_id,
        branchId: selectionData.branch_id,
      });

      if (canLoad) {
        GetLeaveList();
      } else {
        setLeaveList([]);
      }
    } else {
      GetLeaveList();
    }
  }, [
    isAdminUser,
    selectionData.vendor_id,
    selectionData.branch_id,
    selectionData.status,
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    GetLeaveList,
  ]);

  const onStatusFilterChange = (e) => {
    setSelectionData((prev) => ({
      ...prev,
      status: e.target.value,
    }));
  };

  const UpdateLeaveStatus = (id, status) => {
    setRemarkModal({
      show: true,
      id,
      status,
      remark: '',
      error: '',
    });
  };

  const ConfirmLeaveStatus = async () => {
    const isReject = remarkModal.status === 'rejected';

    if (isReject && !remarkModal.remark.trim()) {
      setRemarkModal((prev) => ({
        ...prev,
        error: 'Reject reason is required',
      }));
      return;
    }

    try {
      const payload = {
        id: remarkModal.id,
        status: remarkModal.status,
        admin_remark: remarkModal.remark.trim(),
      };

      const res = await apiService.put(
        'api/AttendanceMaster/update-leave-status',
        payload,
      );

      if (res?.status === true) {
        toast.success(res.message);

        setRemarkModal({
          show: false,
          id: 0,
          status: '',
          remark: '',
          error: '',
        });

        GetLeaveList();
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  const CancelLeaveStatus = () => {
    setRemarkModal({
      show: false,
      id: 0,
      status: '',
      remark: '',
      error: '',
    });
  };

  const DeleteLeaveRequest = async (id) => {
    if (
      !window.confirm('Are you sure you want to delete this leave request?')
    ) {
      return;
    }

    try {
      const res = await apiService.delete(
        `api/AttendanceMaster/delete-leave?id=${id}`,
      );

      if (res?.status === true) {
        toast.success(res.message);
        GetLeaveList();
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  return (
    <>
      <CustomModal
        show={remarkModal.show}
        title={remarkModal.status === 'approved' ? 'Approve' : 'Reject'}
        confirmText={remarkModal.status === 'approved' ? 'Approve' : 'Reject'}
        confirmClassName={
          remarkModal.status === 'approved'
            ? 'btn btn-success'
            : 'btn btn-danger'
        }
        onConfirm={ConfirmLeaveStatus}
        onCancel={CancelLeaveStatus}
        content={
          <div className='w-100'>
            <label className='form-label'>
              {remarkModal.status === 'approved'
                ? 'Approval Remark'
                : 'Reject Reason'}
            </label>

            <textarea
              className='form-control'
              rows='4'
              value={remarkModal.remark}
              placeholder={
                remarkModal.status === 'approved'
                  ? 'Enter approval remark optional'
                  : 'Enter reject reason'
              }
              onChange={(e) =>
                setRemarkModal((prev) => ({
                  ...prev,
                  remark: e.target.value,
                  error: '',
                }))
              }
            />

            {remarkModal.error && (
              <div className='text-danger mt-2'>{remarkModal.error}</div>
            )}

            {remarkModal.status === 'approved' && (
              <small className='text-muted mt-2 d-block'>
                Approval remark is optional.
              </small>
            )}
          </div>
        }
      />

      <div className='text-right mb-2'>
        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> Add Leave Request
        </Button>
      </div>

      {isAdminUser && (
        <VendorBranchSelector
          user={user}
          selectionData={selectionData}
          setSelectionData={setSelectionData}
          errorMessage={errorMessage}
          accessButton={true}
        />
      )}

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-calendar-check mr-2' /> Leave Requests
        </CardTitle>

        <CardBody>
          {isAdminUser && (
            <Row>
              <FormGroup className='col-sm-3'>
                <Label>Status</Label>
                <Input
                  type='select'
                  name='status'
                  value={selectionData.status}
                  onChange={onStatusFilterChange}
                >
                  <option value='pending'>Pending</option>
                  <option value='approved'>Approved</option>
                  <option value='rejected'>Rejected</option>
                  <option value='all'>All</option>
                </Input>
              </FormGroup>

              <FormGroup className='col-sm-2 d-flex align-items-end'>
                <Button color='primary' onClick={GetLeaveList}>
                  Search
                </Button>
              </FormGroup>
            </Row>
          )}

          <Table striped hover responsive>
            <thead>
              <tr>
                <th>#</th>
                {isAdminUser && <th>Employee</th>}
                {isAdminUser && <th>Code</th>}
                {isAdminUser && <th>Designation</th>}
                <th>Leave Date</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Admin Remark</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {leaveList.length > 0 ? (
                leaveList.map((x, i) => {
                  const st = String(x.status || '').toLowerCase();

                  return (
                    <tr key={x.id}>
                      <td>{i + 1}</td>

                      {isAdminUser && <td>{x.employeeName || '-'}</td>}
                      {isAdminUser && <td>{x.employeeCode || '-'}</td>}
                      {isAdminUser && <td>{x.designationName || '-'}</td>}

                      <td>{formatDate(x.leave_date)}</td>
                      <td>{x.remark}</td>

                      <td>
                        <span className={getStatusBadge(x.status)}>
                          {x.status || 'pending'}
                        </span>
                      </td>

                      <td>{x.admin_remark || '-'}</td>

                      <td>
                        {isAdminUser && st === 'pending' ? (
                          <>
                            <Button
                              size='sm'
                              color='success'
                              className='me-2'
                              onClick={() =>
                                UpdateLeaveStatus(x.id, 'approved')
                              }
                            >
                              Approve
                            </Button>

                            <Button
                              size='sm'
                              color='danger'
                              onClick={() =>
                                UpdateLeaveStatus(x.id, 'rejected')
                              }
                            >
                              Reject
                            </Button>
                          </>
                        ) : !isAdminUser && st === 'pending' ? (
                          <i
                            className='bi bi-trash text-danger'
                            style={{ cursor: 'pointer' }}
                            onClick={() => DeleteLeaveRequest(x.id)}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isAdminUser ? '9' : '6'} className='text-center'>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default LeaveRequestList;
